import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Info } from "lucide-react";
import AppShell from "../../components/AppShell";
import { api, formatErr } from "../../lib/api";
import { REVIEW_STATUS } from "../../lib/planReview";
import { Card, Pill } from "../../components/onboarding/adminUi";
import { AnswersView, HealthView } from "../../components/onboarding/AnswersView";
import { ActionsBar } from "../../components/planreview/ActionsBar";
import { AiRunCard } from "../../components/planreview/AiRunCard";
import { SectionsEditor, TrainingCard, CommentsCard, VersionsCard } from "../../components/planreview/Cards";
import { btn, fmtDate } from "../../components/leads/PriorityBadge";
import { ReviewNutrition } from "../../components/nutrition/ReviewNutrition";

const TABS = [["draft", "Borrador"], ["nutrition", "Nutrición"], ["info", "Información del futbolista"], ["notes", "Comentarios"], ["versions", "Versiones"]];
const LEVEL = { high: "#f87171", medium: "#facc15", info: "#a3a3a3" };

const Alerts = ({ alerts }) => (
  <Card title="Alertas y datos pendientes" testid="pr-alerts-card">
    {alerts.length === 0 ? <div style={{ fontSize: 13, color: "#4ade80" }}>Sin alertas.</div> : alerts.map((a, i) => (
      <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", color: LEVEL[a.level], fontSize: 13, padding: "4px 0" }} data-testid={`pr-alert-${i}`}>
        {a.level === "info" ? <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} /> : <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />}<span>{a.text}</span>
      </div>
    ))}
  </Card>
);

const InfoTab = ({ onbId }) => {
  const [o, setO] = useState(null);
  useEffect(() => { if (onbId) api.get(`/admin/onboardings/${onbId}`).then((x) => setO(x.data)).catch((e) => toast.error(formatErr(e))); }, [onbId]);
  if (!onbId) return <Card title="Información"><div style={{ fontSize: 13, color: "#a3a3a3" }}>Este cliente no tiene formulario de incorporación. Consulta su ficha de cliente.</div></Card>;
  if (!o) return <div style={{ color: "#a3a3a3" }}>Cargando…</div>;
  return (<>
    <div style={{ marginBottom: 12 }}><Link to={`/app/admin/incorporaciones/${o.id}`} style={{ color: "#fff", fontSize: 13 }} data-testid="pr-open-onboarding">Abrir incorporación →</Link></div>
    <HealthView o={o} />
    <AnswersView o={o} />
  </>);
};

export default function AdminPlanReviewDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("draft");
  const load = useCallback(() => api.get(`/admin/plan-reviews/${id}`).then((x) => setR(x.data)).catch((e) => setErr(formatErr(e))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (r?.status !== "draft_generating") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [r?.status, load]);
  const locked = r?.status === "published";
  const manual = () => api.post(`/admin/plan-reviews/${id}/manual-draft`).then(() => { toast.success("Borrador creado"); load(); }).catch((e) => toast.error(formatErr(e)));

  return (
    <AppShell>
      <Link to="/app/admin/planificaciones" style={{ ...btn("transparent"), marginBottom: 18 }} data-testid="pr-back"><ArrowLeft size={14} /> Planificaciones</Link>
      {err && <div style={{ color: "#f87171" }} data-testid="pr-error">{err}</div>}
      {!r && !err && <div style={{ color: "#a3a3a3" }}>Cargando…</div>}
      {r && (<div data-testid="pr-detail">
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
          <h1 style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: "2.2rem", letterSpacing: "0.04em", margin: 0 }} data-testid="pr-name">{r.user?.name}</h1>
          <Pill map={REVIEW_STATUS} k={r.status} testid="pr-detail-status" />
          <span style={{ fontSize: 12, color: "#737373" }}>{r.user?.role === "prospect" ? "Sin acceso todavía" : "Cliente"}</span>
        </div>
        <div style={{ fontSize: 12, color: "#a3a3a3", marginBottom: 14 }}>
          {r.published_at ? `Publicada ${fmtDate(r.published_at)}` : "Aún no publicada: el futbolista ve «pendiente de revisión»"}
          {r.status === "draft_generating" && " · La IA está preparando el borrador (3-6 min). Esta página se actualiza sola."}
        </div>
        <ActionsBar r={r} onChange={load} />
        <Alerts alerts={r.alerts || []} />
        <AiRunCard rid={r.id} status={r.status} incomplete={r.ai_incomplete} />
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: 16, flexWrap: "wrap" }}>
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`pr-tab-${k}`}
              style={{ background: "none", border: "none", borderBottom: `2px solid ${tab === k ? "#E11D2E" : "transparent"}`, color: tab === k ? "#fff" : "#a3a3a3", padding: "10px 14px", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer" }}>{l}</button>
          ))}
        </div>
        {tab === "draft" && (<>
          {locked && <div className="emp-note" style={{ marginBottom: 16 }} data-testid="pr-locked-note">Publicada: no se puede editar. Usa «Modificar (nueva versión)»; la versión actual se conserva en el historial.</div>}
          <TrainingCard r={r} locked={locked} onManual={manual} />
          <SectionsEditor r={r} locked={locked} onSaved={load} />
        </>)}
        {tab === "nutrition" && <ReviewNutrition rid={r.id} userId={r.user_id} onChange={load} />}
        {tab === "info" && <InfoTab onbId={r.onboarding_id} />}
        {tab === "notes" && <CommentsCard r={r} onAdded={load} />}
        {tab === "versions" && <VersionsCard r={r} locked={locked} onChange={load} />}
      </div>)}
    </AppShell>
  );
}
